import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use current origin in production, env variable in development
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin + '/api';
  }
  return (process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';
};
const API_URL = getApiUrl();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('vgt_token'));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('vgt_token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        localStorage.removeItem('vgt_token');
        setToken(null);
        setUser(null);
      }
    } else {
      // Try cookie-based auth
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        // Not authenticated
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('vgt_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role, guideType = null) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      name,
      email,
      password,
      role,
      guide_type: guideType
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('vgt_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    const { user: userData, token: newToken } = response.data;
    if (newToken) {
      localStorage.setItem('vgt_token', newToken);
      setToken(newToken);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore
    }
    localStorage.removeItem('vgt_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = async (updates) => {
    const response = await axios.put(`${API_URL}/auth/me`, updates, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    });
    setUser(response.data);
    return response.data;
  };

  const switchRole = async (newRole) => {
    const response = await axios.put(
      `${API_URL}/auth/role`,
      { role: newRole },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      }
    );
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    updateUser,
    switchRole,
    refreshUser: checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
